import { Group, NumberInput, Tooltip } from '@mantine/core';
import { ChastiaConfigForm, DeviceTypePairing } from 'components/forms/remote-source/config/types';
import { IconArrowRight, IconColumns3, IconCopy, IconTrash } from '@tabler/icons-react';
import { DeviceTypeSelect } from 'components/selects/DeviceTypeSelect';
import { useCallback } from 'react';
import { useConfirm } from 'components/modals/message/MessageProvider';
import { useColumnPairingModal } from 'components/forms/remote-source/config/chastia-config-form/device-types/columns/ColumnPairingProvider';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';

interface DeviceTypePairingRowProps {
  form: ChastiaConfigForm;
  item: DeviceTypePairing;
  index: number;
}

/**
 * A single row for Device Type Column Pairing configuration.
 */
export default function DeviceTypePairingRow({ form, item, index }: DeviceTypePairingRowProps) {
  const { confirm } = useConfirm();
  const { openColumnPairingModal } = useColumnPairingModal();

  const configRow = useCallback(() => {
    openColumnPairingModal({
      initialValues: item.columns,
      onSubmit: (values) => {
        form.setFieldValue(`types.${index}.columns`, values);
      },
    });
  }, [form, item, index]);

  const cloneRow = useCallback(() => {
    form.insertListItem('types', { ...item }, index);
  }, [form, item, index]);

  const removeRow = useCallback(() => {
    if (!item.chastiaId && !item.deviceTypeId && item.columns.length === 0) {
      form.removeListItem('types', index);
    } else {
      confirm({
        title: 'Odstrániť riadok?',
        content: 'Naozaj chcete odstrániť tento riadok mapovania typov zariadení?',
        onConfirm: () => form.removeListItem('types', index),
      });
    }
  }, [form, index]);

  return (
    <Group key={item.key} className="grid max-w-[1216px] grid-cols-[1fr_24px_1fr_288px] items-center gap-y-0">
      <NumberInput size="md" {...form.getInputProps(`types.${index}.chastiaId`)} />
      <IconArrowRight stroke={1.5} />
      <DeviceTypeSelect size="md" listSubtypes {...form.getInputProps(`types.${index}.deviceTypeId`)} />
      <Group spacing={8} noWrap>
        <Button leftIcon={<IconColumns3 />} variant="secondary" size="md" onClick={configRow}>
          Stĺpcové údaje ({item.columns.length})
        </Button>
        <Tooltip label="Duplikovať riadok" openDelay={500} withArrow={false}>
          <ActionIcon variant="subtle-gray" size="md" onClick={cloneRow}>
            <IconCopy />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Odstrániť riadok" openDelay={500} withArrow={false}>
          <ActionIcon variant="subtle" size="md" onClick={removeRow}>
            <IconTrash />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Group>
  );
}
